import React, {useContext} from 'react'
import {InformationCircleIcon} from '@heroicons/react/24/outline'
import AppContext from '../../appContext'
import LimeProductResponse from '../../controllers/LimeProductResponse'
import {formatProductField, visibleChemicalFields} from './limeProductFields'

interface ProductCharacteristicsProps {
    product: LimeProductResponse | null | undefined
}

/**
 * Shows an info icon next to a selected lime product. Hovering or focusing it reveals a
 * compact card of that product's characteristics — the same fields captured when the
 * product is created — so a program can be set up without access to the Products section.
 */
const ProductCharacteristics: React.FC<ProductCharacteristicsProps> = ({product}) => {
    const context = useContext(AppContext)
    if (!product) return null

    const fields = visibleChemicalFields(product.type)

    return (
        <span className="group relative inline-flex align-middle ml-2">
            <button
                type="button"
                aria-label={context.word('product_characteristics')}
                className="text-primary-600 hover:text-primary-800 focus:outline-none focus:text-primary-800"
            >
                <InformationCircleIcon className="h-5 w-5"/>
            </button>

            <div className="hidden group-hover:block group-focus-within:block absolute left-full top-0 ml-2 z-30 w-60 rounded border border-gray-300 bg-white p-3 text-sm shadow-lg">
                <div className="flex items-baseline justify-between border-b border-gray-200 pb-1 mb-2">
                    <span className="font-bold text-primary-800">{product.name}</span>
                    <span className="text-xs text-gray-500">{product.type}</span>
                </div>

                {fields.length > 0 && (
                    <table className="w-full">
                        <tbody>
                            {fields.map(f => (
                                <tr key={f.key}>
                                    <td className="pr-2 text-gray-600 whitespace-nowrap">{context.word(f.key)}</td>
                                    <td className="text-right tabular-nums">{formatProductField(product, f.key, f.percent)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </span>
    )
}

export default ProductCharacteristics
